/**
 * <UserAvatar /> — Radix Avatar wrapper standardising the way we render
 * a user's picture across the app (nav, profile, message slides…).
 *
 * Decisions:
 *   - The component is **dumb**: it takes a `src` and a `name`. URL
 *     building lives in `lib/avatars.ts` (testable in isolation).
 *   - Initials are computed automatically via `buildInitials(name)` and
 *     used as the Radix `fallback`.
 *   - `version` appends a `?v=` cache-buster (useful right after upload).
 *   - All Radix Avatar props (size, radius, color, style, …) are passed
 *     through so callers can theme it per context.
 */

import { Avatar, type AvatarProps } from "@radix-ui/themes";
import { buildInitials, withAvatarVersion } from "@/lib/avatars";

export interface UserAvatarProps extends Omit<AvatarProps, "fallback" | "src"> {
  /** Direct image URL. When falsy, the component renders initials. */
  src?: string | null;
  /** Display name used to compute fallback initials (and as alt text). */
  name?: string | null;
  /** Cache-bust appended as `?v=` — typically set after avatar upload. */
  version?: number | string | null;
  /** Override the computed initials (rare — e.g. for "?" placeholders). */
  fallback?: string;
}

export function UserAvatar({
  src,
  name,
  version,
  fallback,
  alt,
  ...rest
}: UserAvatarProps) {
  const finalSrc = withAvatarVersion(src ?? null, version) ?? undefined;
  const initials = fallback ?? buildInitials(name);

  return (
    <Avatar
      {...rest}
      src={finalSrc}
      alt={alt ?? name ?? undefined}
      fallback={initials}
    />
  );
}
